import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function useQueryState(paramName: string, initialValue?: string | null): [string, Dispatch<SetStateAction<string | null>>] {
  const { search } = useLocation();
  const navigate = useNavigate();
  const defaultParam = new URLSearchParams(search).get(paramName);
  const [state, setState] = useState(defaultParam || initialValue || null);

  const popstateHandler = useCallback(() => {
    const searchParts = window.location.href.split('?');
    const newValue = new URLSearchParams(searchParts[searchParts.length - 1]).get(paramName);

    if (state !== newValue) {
      setState(newValue);
    }
  }, [paramName, state]);

  useEffect(() => {
    window.addEventListener('popstate', popstateHandler);

    return () => {
      window.removeEventListener('popstate', popstateHandler);
    };
  }, [popstateHandler]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (!state) {
      if (params.get(paramName)) {
        params.delete(paramName);
        params.sort();
        navigate({
          search: params.toString(),
        });
      }
    } else if (state !== params.get(paramName)) {
      params.set(paramName, state);
      params.sort();
      navigate({
        search: params.toString(),
      });
    }
  }, [navigate, paramName, state, search, initialValue]);

  return [state || '', setState as Dispatch<SetStateAction<string | null>>];
}
